import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    Sparkles, X, Send, Loader2, Trash2, CheckCircle2,
    ClipboardList, Mail, ShieldCheck, Mic, Square, FileText,
    StickyNote, CalendarClock, Volume2,
} from "lucide-react";

const SUMMARIZE_PROMPT =
    "Give me a professional, actionable summary of my whole portfolio — client notes, tasks, and evidence status — with recommended follow-up tasks.";

const ACTION_META = {
    create_task: { icon: ClipboardList, label: "Create task" },
    send_briefing: { icon: Mail, label: "Send briefing email" },
    create_note: { icon: StickyNote, label: "Save note to workspace" },
    send_client_email: { icon: Mail, label: "Email the client" },
    schedule_meeting: { icon: CalendarClock, label: "Schedule meeting" },
};

function actionDetail(action) {
    switch (action.type) {
        case "create_task":
            return `${action.client_name}: “${action.title}”${action.due_date ? ` · due ${action.due_date}` : ""}`;
        case "create_note":
            return `${action.client_name}: “${action.title}” — ${action.content?.slice(0, 140)}${action.content?.length > 140 ? "…" : ""}`;
        case "send_client_email":
            return `To ${action.client_name} · “${action.subject}” — ${action.body?.slice(0, 140)}${action.body?.length > 140 ? "…" : ""}`;
        case "schedule_meeting":
            return `${action.client_name}: “${action.title}” · ${String(action.starts_at).slice(0, 16).replace("T", " ")} UTC · ${action.duration_min} min`;
        default:
            return null;
    }
}

function ActionCard({ action, onConfirm, onDismiss, busy }) {
    const meta = ACTION_META[action.type] || { icon: ShieldCheck, label: action.type };
    const Icon = meta.icon;
    return (
        <div
            className="rounded-xl border border-border bg-background/60 p-3 mt-2"
            data-testid={`firm-agent-action-card-${action.type}`}
        >
            <div className="flex items-center gap-2 text-sm font-medium">
                <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                {meta.label}
            </div>
            {actionDetail(action) && (
                <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                    {actionDetail(action)}
                </div>
            )}
            <div className="flex gap-2 mt-2">
                <Button
                    size="sm"
                    onClick={() => onConfirm(action)}
                    disabled={busy}
                    className="rounded-md font-display h-8"
                    data-testid="firm-agent-action-confirm"
                >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                    Confirm & run
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDismiss(action)}
                    disabled={busy}
                    className="rounded-md font-display h-8"
                    data-testid="firm-agent-action-dismiss"
                >
                    Dismiss
                </Button>
            </div>
        </div>
    );
}

export default function FirmAgentDrawer() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [dismissed, setDismissed] = useState({}); // action id -> true

    const visibleActions = useMemo(() => {
        const byMsg = {};
        for (const msg of messages) {
            byMsg[msg.id] = (msg.proposed_actions || []).filter((a) => !dismissed[a.id]);
        }
        return byMsg;
    }, [messages, dismissed]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [speakingId, setSpeakingId] = useState(null); // msg id playing or 'loading:<id>'
    const audioRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const bottomRef = useRef(null);

    const isConsultant = user?.role === "consultant";

    useEffect(() => {
        if (!open || !isConsultant) return;
        (async () => {
            try {
                const { data } = await api.get("/firm/agent/messages");
                setMessages(data.messages || []);
            } catch { /* drawer just starts empty */ }
        })();
    }, [open, isConsultant]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    // Push page content left while the drawer is open (desktop) so no
    // information is ever hidden behind the chat panel. Padding goes on #root,
    // NOT <body> — Radix's scroll-lock also mutates body padding-right, and the
    // two fighting over it can strand a locked body (page appears frozen).
    useEffect(() => {
        const root = document.getElementById("root");
        if (!root) return undefined;
        const apply = () => {
            if (open && window.innerWidth >= 1024) {
                root.style.paddingRight = "444px";
                root.style.transition = "padding-right 0.25s ease";
            } else {
                root.style.paddingRight = "";
            }
        };
        apply();
        window.addEventListener("resize", apply);
        return () => {
            window.removeEventListener("resize", apply);
            root.style.paddingRight = "";
            root.style.transition = "";
        };
    }, [open]);

    if (!isConsultant) return null;

    const stopSpeaking = () => {
        audioRef.current?.pause();
        audioRef.current = null;
        setSpeakingId(null);
    };

    const speak = async (m) => {
        if (speakingId === m.id || speakingId === `loading:${m.id}`) {
            stopSpeaking();
            return;
        }
        stopSpeaking();
        setSpeakingId(`loading:${m.id}`);
        try {
            const plain = m.content.replace(/[#*_`>|]/g, "").slice(0, 4000);
            const res = await api.post("/firm/agent/speak", { text: plain }, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "audio/mpeg" }));
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => { URL.revokeObjectURL(url); stopSpeaking(); };
            await audio.play();
            setSpeakingId(m.id);
        } catch (err) {
            stopSpeaking();
            toast.error(formatApiError(err?.response?.data?.detail) || "Voice playback failed");
        }
    };

    const send = async (e, overrideText) => {
        e?.preventDefault();
        const text = (overrideText || input).trim();
        if (!text || sending) return;
        setInput("");
        setSending(true);
        setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
        try {
            const { data } = await api.post("/firm/agent/chat", { text });
            setMessages((m) => [...m, data.message]);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Agent unavailable");
        } finally {
            setSending(false);
        }
    };

    const confirmAction = async (action) => {
        setActionBusy(true);
        try {
            const { data } = await api.post("/firm/agent/actions/execute", {
                type: action.type,
                client_id: action.client_id,
                title: action.title,
                due_date: action.due_date,
                content: action.content,
                subject: action.subject,
                body: action.body,
                starts_at: action.starts_at,
                duration_min: action.duration_min,
            });
            setDismissed((d) => ({ ...d, [action.id]: true }));
            setMessages((m) => [...m, data.message]);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Action failed");
        } finally {
            setActionBusy(false);
        }
    };

    const dismissAction = (action) =>
        setDismissed((d) => ({ ...d, [action.id]: true }));

    const clearChat = async () => {
        try {
            await api.delete("/firm/agent/messages");
            setMessages([]);
            setDismissed({});
        } catch { /* ignore */ }
    };

    const toggleMic = async () => {
        if (recording) {
            recorderRef.current?.stop();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                setRecording(false);
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                if (blob.size < 1000) return; // too short to transcribe
                setTranscribing(true);
                try {
                    const form = new FormData();
                    form.append("audio", blob, "voice-prompt.webm");
                    const { data } = await api.post("/firm/agent/transcribe", form, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    setInput((prev) => (prev ? `${prev} ${data.text}` : data.text));
                } catch (err) {
                    toast.error(formatApiError(err?.response?.data?.detail) || "Transcription failed");
                } finally {
                    setTranscribing(false);
                }
            };
            recorderRef.current = rec;
            rec.start();
            setRecording(true);
        } catch {
            toast.error("Microphone unavailable — check browser permissions.");
        }
    };

    return (
        <>
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    title="Firm Agent — portfolio AI"
                    className="fixed bottom-5 right-5 z-40 h-12 w-12 grid place-items-center rounded-full bg-foreground text-background shadow-lg hover:opacity-90 transition-opacity"
                    data-testid="firm-agent-fab"
                >
                    <Sparkles className="h-5 w-5" strokeWidth={1.5} />
                </button>
            )}
            {open && (
                <div
                    className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[420px] h-[85vh] sm:h-[620px] flex flex-col rounded-t-2xl sm:rounded-2xl border border-border bg-card shadow-2xl"
                    data-testid="firm-agent-drawer"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-foreground text-background grid place-items-center">
                                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                            </div>
                            <div>
                                <div className="font-display font-semibold leading-tight">Firm Agent</div>
                                <div className="mono-label text-muted-foreground">
                                    Whole-portfolio AI · actions need your confirmation
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={clearChat}
                                className="rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                title="Clear conversation"
                                data-testid="firm-agent-clear"
                            >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                data-testid="firm-agent-close"
                            >
                                <X className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="firm-agent-messages">
                        {messages.length === 0 && (
                            <div className="text-sm text-muted-foreground p-4 rounded-xl border border-dashed border-border/70">
                                Ask me anything across your whole portfolio — e.g.
                                “Which clients are furthest behind?”, “Create a task
                                for Acme to renew their pen test”, or “Send me this
                                week's briefing.” I always ask before doing anything.
                            </div>
                        )}
                        {messages.map((m) => (
                            <div key={m.id} data-testid={`firm-agent-msg-${m.role}`}>
                                {m.role === "user" ? (
                                    <div className="ml-8 rounded-2xl rounded-br-sm bg-foreground text-background px-4 py-2.5 text-sm whitespace-pre-wrap">
                                        {m.content}
                                    </div>
                                ) : m.role === "action_result" ? (
                                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm">
                                        {m.content}
                                    </div>
                                ) : (
                                    <div className="mr-8 rounded-2xl rounded-bl-sm bg-muted/60 px-4 py-2.5 text-sm relative">
                                        <button
                                            type="button"
                                            onClick={() => speak(m)}
                                            title={speakingId === m.id ? "Stop playback" : "Read aloud"}
                                            className={`absolute -right-2 -top-2 h-7 w-7 rounded-full grid place-items-center border shadow-sm transition-colors ${
                                                speakingId === m.id
                                                    ? "bg-foreground text-background border-foreground"
                                                    : "bg-card text-muted-foreground border-border hover:text-foreground"
                                            }`}
                                            data-testid={`firm-agent-speak-${m.id}`}
                                        >
                                            {speakingId === `loading:${m.id}` ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                                            ) : speakingId === m.id ? (
                                                <Square className="h-3 w-3" strokeWidth={1.5} />
                                            ) : (
                                                <Volume2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                            )}
                                        </button>
                                        <div className="agent-md">
                                            <ReactMarkdown>{m.content}</ReactMarkdown>
                                        </div>
                                        {(visibleActions[m.id] || [])
                                            .map((a) => (
                                                <ActionCard
                                                    key={a.id}
                                                    action={a}
                                                    onConfirm={confirmAction}
                                                    onDismiss={dismissAction}
                                                    busy={actionBusy}
                                                />
                                            ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {sending && (
                            <div className="mr-8 rounded-2xl bg-muted/60 px-4 py-3 text-sm flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                                Thinking across your portfolio…
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    <div className="px-3 pt-2 flex gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => send(null, SUMMARIZE_PROMPT)}
                            disabled={sending}
                            className="mono-label rounded-full border border-border px-3 py-1.5 text-muted-foreground hover:border-foreground/50 hover:text-foreground transition-colors flex items-center gap-1.5"
                            data-testid="firm-agent-chip-summarize"
                        >
                            <FileText className="h-3 w-3" strokeWidth={1.5} />
                            Summarize portfolio
                        </button>
                        <button
                            type="button"
                            onClick={() => send(null, "Send me this week's briefing email.")}
                            disabled={sending}
                            className="mono-label rounded-full border border-border px-3 py-1.5 text-muted-foreground hover:border-foreground/50 hover:text-foreground transition-colors flex items-center gap-1.5"
                            data-testid="firm-agent-chip-briefing"
                        >
                            <Mail className="h-3 w-3" strokeWidth={1.5} />
                            Draft briefing
                        </button>
                    </div>

                    <form onSubmit={send} className="p-3 border-t border-border flex gap-2 items-center">
                        <button
                            type="button"
                            onClick={toggleMic}
                            disabled={transcribing || sending}
                            title={recording ? "Stop recording" : "Dictate your command"}
                            className={`h-12 w-12 rounded-full grid place-items-center shrink-0 border transition-colors ${
                                recording
                                    ? "bg-red-500 border-red-500 text-white animate-pulse"
                                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/50"
                            }`}
                            data-testid="firm-agent-mic"
                        >
                            {transcribing ? (
                                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                            ) : recording ? (
                                <Square className="h-4 w-4" strokeWidth={1.5} />
                            ) : (
                                <Mic className="h-4 w-4" strokeWidth={1.5} />
                            )}
                        </button>
                        <Input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={recording ? "Listening… tap ■ to stop" : "Ask, dictate or instruct your firm agent…"}
                            className="h-12 rounded-full"
                            data-testid="firm-agent-input"
                        />
                        <Button
                            type="submit"
                            disabled={sending || !input.trim()}
                            className="h-12 w-12 rounded-full p-0 shrink-0"
                            data-testid="firm-agent-send"
                        >
                            <Send className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                    </form>
                </div>
            )}
        </>
    );
}
